import type { CSSProperties } from 'react'
import type { CropRect, GraphicPage, GraphicPageBlock, GraphicPageTree } from '../../../graphicPageTypes'
import './graphic-renderer.css'

export type Device = 'desktop' | 'mobile'

export interface ImageResolver {
  /** Resolves a block's own `asset_id` (or the page cover's
   * `cover_asset_id`) to a real, loadable URL. In the authenticated
   * Editor this wraps `siteAssetVariantUrl`; in the Public Renderer the
   * URL is already embedded on the block/page by `public_site.py`'s
   * `_resolve_graphic_tree_urls` (spec #24 — never a local path). */
  block: (block: GraphicPageBlock, role: 'web' | 'original') => string | null
  page: (page: GraphicPage, role: 'web' | 'original') => string | null
  entryCover: (entry: { cover_asset_id: string | null; cover_url?: string | null }, role: 'web' | 'original') => string | null
}

/** Hero title font families (LiangCollection `hero_fonts.css` bundled
 * actual webfont files for these 4 choices; Vistoera doesn't ship the
 * same museum-branded font binaries, so this maps to equivalent system
 * font-stack fallbacks — a REPLACED_BY_VISTOERA_ARCHITECTURE substitution
 * for the asset, not a dropped field: `font_family` is still fully
 * captured, saved, and rendered differently per choice.) */
const HERO_FONT_STACKS: Record<string, string | undefined> = {
  'system-sans': undefined,
  'source-han-sans': '"PingFang SC", "Noto Sans CJK SC", "Microsoft YaHei", sans-serif',
  'source-han-serif': '"Songti SC", "Noto Serif CJK SC", "SimSun", serif',
  'lxgw-wenkai': '"LXGW WenKai", "Kaiti SC", "STKaiti", cursive, serif',
}

function cropStyle(crop: CropRect | null | undefined): CSSProperties {
  if (!crop) return { objectPosition: '50% 50%' }
  const fx = crop.focal_x ?? 50
  const fy = crop.focal_y ?? 50
  return { objectPosition: `${fx}% ${fy}%` }
}

function aspectFor(aspect: string | undefined): string | undefined {
  switch (aspect) {
    case '16:9':
      return '16 / 9'
    case '4:3':
      return '4 / 3'
    case '3:2':
      return '3 / 2'
    case '1:1':
      return '1 / 1'
    case '3:4':
      return '3 / 4'
    default:
      return undefined
  }
}

function layoutMaxWidth(layout: string | undefined): number | undefined {
  if (layout === 'wide') return 960
  if (layout === 'full') return undefined
  return 720
}

function EntryCard({
  entry,
  resolveEntryCover,
  fallbackLabel,
}: {
  entry: { id: string; label: string; slug: string; content_type: string; cover_asset_id: string | null; cover_url?: string | null } | null
  resolveEntryCover: ImageResolver['entryCover']
  fallbackLabel: string
}) {
  if (!entry) {
    return (
      <div className="gr-ref-card gr-ref-card-empty">
        <span>{fallbackLabel}（尚未选择关联内容）</span>
      </div>
    )
  }
  const cover = resolveEntryCover(entry, 'web')
  return (
    <div className="gr-ref-card">
      <div className="gr-ref-card-cover">{cover && <img src={cover} alt={entry.label} loading="lazy" />}</div>
      <div className="gr-ref-card-title">{entry.label}</div>
    </div>
  )
}

function BlockRenderer({
  block,
  device,
  resolveImage,
}: {
  block: GraphicPageBlock
  device: Device
  resolveImage: ImageResolver
}) {
  const c = block.content || {}
  switch (block.block_type) {
    case 'heading': {
      const level = Number(c.level) || 2
      const Tag = (`h${Math.min(4, Math.max(2, level))}` as unknown) as 'h2' | 'h3' | 'h4'
      return <Tag className="gr-heading">{String(c.text || '')}</Tag>
    }
    case 'text':
      return <div className="gr-rich-text" dangerouslySetInnerHTML={{ __html: String(c.html || '') }} />
    case 'quote':
      return (
        <blockquote className="gr-quote">
          <p>{String(c.text || '')}</p>
          {c.citation ? <cite>{String(c.citation)}</cite> : null}
        </blockquote>
      )
    case 'divider':
      return <hr className="gr-divider" />
    case 'spacer': {
      const h = c.height === 'sm' ? 24 : c.height === 'lg' ? 96 : 56
      return <div style={{ height: h }} />
    }
    case 'image': {
      const url = resolveImage.block(block, 'web')
      const aspect = aspectFor(String(c.aspect || 'original'))
      const maxWidth = layoutMaxWidth(String(c.layout || 'standard'))
      const align = String(c.align || 'center')
      return (
        <figure
          className="gr-image-block"
          style={{ maxWidth, marginLeft: align === 'right' ? 'auto' : align === 'center' ? 'auto' : 0, marginRight: align === 'left' ? 'auto' : 'auto' }}
        >
          {url ? (
            <img src={url} alt={String(c.alt || '')} style={{ aspectRatio: aspect, ...cropStyle(c.crop as CropRect) }} loading="lazy" />
          ) : (
            <div className="gr-image-placeholder" style={{ aspectRatio: aspect || '16 / 9' }} />
          )}
          {Boolean(c.caption || c.source || c.copyright) && (
            <figcaption>
              {String(c.caption || '')} {c.source ? <span className="gr-source">来源：{String(c.source)}</span> : null}
              {c.copyright ? <span className="gr-copyright">© {String(c.copyright)}</span> : null}
            </figcaption>
          )}
        </figure>
      )
    }
    case 'collection_item': {
      const url = resolveImage.block(block, 'web')
      const entry = block.linked_entry
      return (
        <figure className="gr-image-block" style={{ maxWidth: layoutMaxWidth(String(c.layout || 'standard')) }}>
          {url ? <img src={url} alt="" style={cropStyle(c.crop as CropRect)} loading="lazy" /> : entry?.cover_url ? (
            <img src={entry.cover_url} alt={entry.label} loading="lazy" />
          ) : (
            <div className="gr-image-placeholder" />
          )}
          <figcaption>
            {c.custom_caption ? String(c.custom_caption) : entry ? (c.show_title !== false ? entry.label : '') : '（未关联条目）'}
          </figcaption>
        </figure>
      )
    }
    case 'exhibition_ref':
      return <EntryCard entry={block.linked_entry} resolveEntryCover={resolveImage.entryCover} fallbackLabel="展览卡片" />
    case 'research_ref':
      return <EntryCard entry={block.linked_entry} resolveEntryCover={resolveImage.entryCover} fallbackLabel="研究卡片" />
    case 'featured_collection':
    case 'featured_exhibition':
    case 'featured_research': {
      const items = block.resolved_items || []
      return (
        <div className={`gr-featured-grid device-${device}`}>
          {items.length === 0 ? (
            <div className="gr-ref-card-empty">暂无精选内容</div>
          ) : (
            items.map((it) => <EntryCard key={it.id} entry={it} resolveEntryCover={resolveImage.entryCover} fallbackLabel="" />)
          )}
        </div>
      )
    }
    case 'video': {
      const url = String(c.external_url || '')
      // LiangCollection parity: video blocks carry an optional poster
      // image shown before playback starts (`content_media_file.poster_image`
      // in the source — see LIANGCOLLECTION_BLOCK_TYPE_CATALOG.md #9).
      // Vistoera reuses the block's own `asset_id` FK for this (no
      // second media system), resolved the same way every other
      // image-bearing block resolves it.
      const poster = resolveImage.block(block, 'web') || undefined
      return (
        <div className="gr-video-block">
          {url ? (
            /youtube|vimeo|youku|bilibili/i.test(url) ? (
              <div className="gr-video-embed-wrap">
                <iframe src={url} title={String(c.title || 'video')} allowFullScreen />
              </div>
            ) : (
              <video src={url} poster={poster} controls={c.controls !== false} />
            )
          ) : (
            <div className="gr-image-placeholder" />
          )}
          {c.title ? <p className="gr-video-title">{String(c.title)}</p> : null}
          {c.caption ? <p className="gr-caption-text">{String(c.caption)}</p> : null}
        </div>
      )
    }
    case 'audio':
      return (
        <div className="gr-audio-block">
          {c.title ? <p className="gr-video-title">{String(c.title)}</p> : null}
          <audio controls src={String(c.external_url || '')} />
          {c.caption ? <p className="gr-caption-text">{String(c.caption)}</p> : null}
          {c.transcript ? <details className="gr-transcript"><summary>文字稿</summary><p>{String(c.transcript)}</p></details> : null}
        </div>
      )
    case 'hero': {
      const crop = (device === 'mobile' ? c.crop_mobile : c.crop_desktop) as CropRect | undefined
      const url = resolveImage.block(block, 'web')
      const heightClass = c.height_mode === 'large' ? 'is-large' : c.height_mode === 'standard' ? 'is-standard' : 'is-fullscreen'
      return (
        <div
          className={`gr-hero-block ${heightClass} pos-${c.text_position || 'left-bottom'} overlay-${c.overlay_strength || 'medium'} color-${c.text_color || 'white'}`}
        >
          {url ? <img className="gr-hero-img" src={url} style={cropStyle(crop)} alt="" /> : <div className="gr-hero-img gr-image-placeholder" />}
          <div className="gr-hero-overlay" />
          <div
            className={`gr-hero-content title-${c.title_size || 'medium'}`}
            style={{
              textAlign: (c.text_align as CSSProperties['textAlign']) || undefined,
              fontFamily: HERO_FONT_STACKS[String(c.font_family || 'system-sans')],
            }}
          >
            {c.title ? <h2>{String(c.title)}</h2> : null}
            {c.subtitle ? <p>{String(c.subtitle)}</p> : null}
          </div>
        </div>
      )
    }
    default:
      return <div className="gr-unknown-block">未知模块类型：{block.block_type}</div>
  }
}

/** Canonical Graphic Page Tree renderer — used identically by the
 * Editor's "Draft Preview" (整页 Preview, reading live rows) and the
 * Public Renderer (reading `published_snapshot_json`), spec #20's "同一
 * 内容结构" requirement. Only the `ImageResolver` differs between the two
 * callers (authenticated vs already-public URLs). */
export default function GraphicTreeRenderer({
  tree,
  device,
  resolveImage,
}: {
  tree: GraphicPageTree
  device: Device
  resolveImage: ImageResolver
}) {
  const { page, modules } = tree
  const coverUrl = resolveImage.page(page, 'web')
  return (
    <div className={`gr-page device-${device}`}>
      {coverUrl && (
        <div className="gr-page-cover">
          <img src={coverUrl} alt={page.cover_alt_text || page.title} style={cropStyle(device === 'mobile' ? page.cover_crop_mobile : page.cover_crop_desktop)} />
        </div>
      )}
      <header className="gr-page-header">
        <h1>{page.title}</h1>
        {page.subtitle && <p className="gr-page-subtitle">{page.subtitle}</p>}
        {(page.author || page.summary) && (
          <p className="gr-page-meta">
            {page.author && <span>{page.author}</span>}
            {page.summary && <span>{page.summary}</span>}
          </p>
        )}
      </header>
      {modules.length === 0 && <div className="gr-empty-state">此页面暂无内容模块。</div>}
      {modules.map((module) => (
        <section key={module.id} id={module.anchor_slug || module.id} className="gr-module">
          {(module.title || module.subtitle || module.intro) && (
            <div className="gr-module-header">
              {module.title && <h2>{module.title}</h2>}
              {module.subtitle && <p className="gr-module-subtitle">{module.subtitle}</p>}
              {module.intro && <p className="gr-module-intro">{module.intro}</p>}
            </div>
          )}
          <div className="gr-module-blocks">
            {module.blocks.map((block) => (
              <div key={block.id} className={`gr-block gr-block-${block.block_type}`}>
                <BlockRenderer block={block} device={device} resolveImage={resolveImage} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
